// Boxscore reconstruction and rendering helpers.
// Converts roll histories into round summaries and renders a modal boxscore table.

export type BoxscorePhase = 'normal' | 'retribution' | 'sudden_death';

export interface RoundSummary {
  phase: BoxscorePhase;
  round: number;
  suddenDeathRound?: number;
  p1Rolls?: number[];
  p2Rolls?: number[];
  p1Points: number;
  p2Points: number;
  p1CumulativeScore: number;
  p2CumulativeScore: number;
  p1Skeech?: boolean;
  p2Skeech?: boolean;
  busts?: Array<{ player: 1 | 2; from: number; to: number }>;
  winner?: 1 | 2;
}

export interface BoxscoreResult {
  summaries: RoundSummary[];
  winner?: 1 | 2;
  finalTotals: {
    phase: BoxscorePhase;
    p1: number;
    p2: number;
    suddenDeathRound?: number;
  };
}

/**
  * Normalize roll history arrays to safe numbers.
  */
export function normalizeRollHistory(raw?: any[] | null): number[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => (typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN))
    .filter((v) => Number.isFinite(v));
}

/**
 * Skeech detection per rules:
 * - Exactly 4 rolls
 * - All non-zero
 * - At least 3 distinct values
 */
export function isSkeechTurnStrict(rolls: number[]): boolean {
  if (rolls.length !== 4) return false;
  if (rolls.some((r) => r === 0)) return false;
  const distinct = new Set(rolls);
  return distinct.size >= 3;
}

function sum(vals: number[]): number {
  return vals.reduce((acc, v) => acc + v, 0);
}

/**
 * Reconstruct a game's round-by-round history from roll arrays.
 */
export function reconstructGame(p1Raw: any[] = [], p2Raw: any[] = []): BoxscoreResult {
  const p1Rolls = normalizeRollHistory(p1Raw);
  const p2Rolls = normalizeRollHistory(p2Raw);

  const summaries: RoundSummary[] = [];

  let p1Idx = 0;
  let p2Idx = 0;
  let p1Score = 0;
  let p2Score = 0;
  let round = 1;
  let sdRound = 1;
  let phase: BoxscorePhase = 'normal';
  let leader: 1 | 2 | null = null;
  let winner: 1 | 2 | undefined;

  const ballsPerTurn = 4;

  const playTurn = (player: 1 | 2, rolls: number[], startIdx: number, currentScore: number) => {
    let idx = startIdx;
    const end = Math.min(startIdx + ballsPerTurn, rolls.length);
    const taken: number[] = [];
    let score = currentScore;
    let busts: Array<{ player: 1 | 2; from: number; to: number }> = [];
    let reached21 = false;

    while (idx < end) {
      const r = rolls[idx];
      const candidate = score + r;
      if (candidate > 21) {
        busts.push({ player, from: candidate, to: 15 });
        score = 15;
      } else {
        score = candidate;
      }
      taken.push(r);
      idx += 1;

      if (score === 21) {
        reached21 = true;
        break;
      }
    }

    const isSkeech = isSkeechTurnStrict(taken);
    if (isSkeech) {
      score = 21;
      reached21 = true;
    }

    const points = phase === 'sudden_death'
      ? (isSkeech ? 21 : taken.reduce((a, b) => a + b, 0))
      : taken.reduce((a, b) => a + b, 0);

    return {
      taken,
      newIdx: idx,
      score,
      points,
      skeech: isSkeech,
      busts,
      reached21
    };
  };

  // Main reconstruction loop
  while (!winner) {
    const prevP1Idx = p1Idx;
    const prevP2Idx = p2Idx;

    if (phase === 'normal') {
      // Player 1 turn
      const p1Turn = playTurn(1, p1Rolls, p1Idx, p1Score);
      p1Idx = p1Turn.newIdx;
      p1Score = p1Turn.score;

      // If P1 reached 21, move to retribution (or SD if P2 already 21, unlikely)
      if (p1Turn.reached21 && p2Score < 21) {
        summaries.push({
          phase: 'normal',
          round,
          p1Rolls: p1Turn.taken,
          p2Rolls: [],
          p1Points: p1Turn.points,
          p2Points: 0,
          p1CumulativeScore: p1Score,
          p2CumulativeScore: p2Score,
          p1Skeech: p1Turn.skeech,
          busts: p1Turn.busts
        });
        phase = 'retribution';
        leader = 1;
        round += 1;
      } else {
        // Player 2 turn
        const p2Turn = playTurn(2, p2Rolls, p2Idx, p2Score);
        p2Idx = p2Turn.newIdx;
        p2Score = p2Turn.score;

        summaries.push({
          phase: 'normal',
          round,
          p1Rolls: p1Turn.taken,
          p2Rolls: p2Turn.taken,
          p1Points: p1Turn.points,
          p2Points: p2Turn.points,
          p1CumulativeScore: p1Score,
          p2CumulativeScore: p2Score,
          p1Skeech: p1Turn.skeech,
          p2Skeech: p2Turn.skeech,
          busts: [...p1Turn.busts, ...p2Turn.busts]
        });

        if (p2Turn.reached21 && p1Score < 21) {
          phase = 'retribution';
          leader = 2;
        } else if (p1Score === 21 && p2Score === 21) {
          phase = 'sudden_death';
        }

        round += 1;
      }
    } else if (phase === 'retribution') {
      // Chasing player only
      const chasingPlayer: 1 | 2 = leader === 1 ? 2 : 1;
      const rolls = chasingPlayer === 1 ? p1Rolls : p2Rolls;
      const idx = chasingPlayer === 1 ? p1Idx : p2Idx;
      const score = chasingPlayer === 1 ? p1Score : p2Score;
      const turn = playTurn(chasingPlayer, rolls, idx, score);

      if (chasingPlayer === 1) {
        p1Idx = turn.newIdx;
        p1Score = turn.score;
      } else {
        p2Idx = turn.newIdx;
        p2Score = turn.score;
      }

      const summary: RoundSummary = {
        phase: 'retribution',
        round,
        p1Rolls: chasingPlayer === 1 ? turn.taken : [],
        p2Rolls: chasingPlayer === 2 ? turn.taken : [],
        p1Points: chasingPlayer === 1 ? turn.points : 0,
        p2Points: chasingPlayer === 2 ? turn.points : 0,
        p1CumulativeScore: p1Score,
        p2CumulativeScore: p2Score,
        p1Skeech: chasingPlayer === 1 ? turn.skeech : undefined,
        p2Skeech: chasingPlayer === 2 ? turn.skeech : undefined,
        busts: turn.busts
      };

      // Determine transition
      if ((chasingPlayer === 1 && p1Score === 21) || (chasingPlayer === 2 && p2Score === 21)) {
        phase = 'sudden_death';
      } else {
        winner = leader === 1 ? 1 : 2;
        summary.winner = winner;
      }

      summaries.push(summary);
      round += 1;
    } else if (phase === 'sudden_death') {
      // Player 1 SD turn
      const p1Turn = playTurn(1, p1Rolls, p1Idx, 0);
      p1Idx = p1Turn.newIdx;
      const p1SDPoints = p1Turn.points;

      // Player 2 SD turn; stop when surpassing p1SDPoints
      let p2Taken: number[] = [];
      let sdSum = 0;
      let sdIdx = p2Idx;
      let p2Busts: Array<{ player: 1 | 2; from: number; to: number }> = [];
      while (sdIdx < p2Rolls.length && p2Taken.length < ballsPerTurn) {
        const r = p2Rolls[sdIdx];
        sdSum += r;
        p2Taken.push(r);
        sdIdx += 1;
        if (sdSum > p1SDPoints) break;
      }
      p2Idx = sdIdx;
      const p2Skeech = isSkeechTurnStrict(p2Taken);
      const p2SDPoints = p2Skeech ? 21 : sdSum;

      // If no rolls remain for both and nothing was taken, bail out
      if (p1Turn.taken.length === 0 && p2Taken.length === 0) {
        break;
      }

      const roundWinner = p1SDPoints > p2SDPoints ? 1 : p2SDPoints > p1SDPoints ? 2 : undefined;

      summaries.push({
        phase: 'sudden_death',
        round,
        suddenDeathRound: sdRound,
        p1Rolls: p1Turn.taken,
        p2Rolls: p2Taken,
        p1Points: p1SDPoints,
        p2Points: p2SDPoints,
        p1CumulativeScore: 21,
        p2CumulativeScore: 21,
        p1Skeech: p1Turn.skeech,
        p2Skeech,
        busts: [...p1Turn.busts, ...p2Busts],
        winner: roundWinner
      });

      if (roundWinner) {
        winner = roundWinner;
      } else {
        sdRound += 1;
      }
    } else {
      break;
    }

    // Safety: if no rolls were consumed and we didn't resolve a winner, break to avoid infinite loop
    if (p1Idx === prevP1Idx && p2Idx === prevP2Idx) {
      break;
    }
  }

  // Derive final totals for display
  const last = summaries[summaries.length - 1];
  let finalTotals: BoxscoreResult['finalTotals'] = { phase, p1: p1Score, p2: p2Score };
  if (last) {
    if (last.phase === 'sudden_death') {
      finalTotals = {
        phase: 'sudden_death',
        p1: last.p1Points,
        p2: last.p2Points,
        suddenDeathRound: last.suddenDeathRound
      };
    } else {
      finalTotals = {
        phase: last.phase,
        p1: last.p1CumulativeScore,
        p2: last.p2CumulativeScore
      };
    }
  }

  // Fallback winner if not set explicitly
  if (!winner && last && last.phase !== 'sudden_death') {
    if (last.p1CumulativeScore > last.p2CumulativeScore) winner = 1;
    else if (last.p2CumulativeScore > last.p1CumulativeScore) winner = 2;
  }

  return { summaries, winner, finalTotals };
}

// ----------------------------
// Modal rendering
// ----------------------------

type BoxscoreRenderOpts = {
  title?: string;
  subtitle?: string;
  p1Name: string;
  p2Name: string;
  p1Rolls?: any[] | null;
  p2Rolls?: any[] | null;
  contextNote?: string;
  showWinnerBadges?: boolean;
};

function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatPoints(
  points: number | undefined,
  skeech?: boolean,
  showDashWhenMissing: boolean = false
): string {
  if (points === undefined || Number.isNaN(points)) {
    return showDashWhenMissing ? '-' : '0';
  }
  const suffix = skeech ? '*' : '';
  return `${points}${suffix}`;
}

export function showBoxscoreModal(opts: BoxscoreRenderOpts) {
  const { title = 'Game Boxscore', subtitle, contextNote } = opts;
  const p1Name = escapeHtml(opts.p1Name || 'Player 1');
  const p2Name = escapeHtml(opts.p2Name || 'Player 2');
  const result = reconstructGame(opts.p1Rolls || [], opts.p2Rolls || []);
  const { summaries, winner, finalTotals } = result;
  const showWinnerBadges = opts.showWinnerBadges ?? true;

  // Build overlay
  const overlay = document.createElement('div');
  overlay.className = 'boxscore-overlay';

  const modal = document.createElement('div');
  modal.className = 'boxscore-modal';
  overlay.appendChild(modal);

  const header = document.createElement('div');
  header.className = 'boxscore-modal-header';
  const titleDiv = document.createElement('div');
  const titleEl = document.createElement('div');
  titleEl.className = 'boxscore-title';
  titleEl.textContent = title;
  titleDiv.appendChild(titleEl);
  if (subtitle) {
    const sub = document.createElement('div');
    sub.className = 'boxscore-subtitle';
    sub.textContent = subtitle;
    titleDiv.appendChild(sub);
  }
  if (contextNote) {
    const note = document.createElement('div');
    note.className = 'boxscore-context';
    note.textContent = contextNote;
    titleDiv.appendChild(note);
  }
  header.appendChild(titleDiv);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'boxscore-close';
  closeBtn.type = 'button';
  closeBtn.textContent = 'x';
  header.appendChild(closeBtn);

  modal.appendChild(header);

  const body = document.createElement('div');
  body.className = 'boxscore-body';

  if (!summaries.length) {
    const empty = document.createElement('div');
    empty.className = 'boxscore-empty';
    empty.textContent = 'No roll history recorded for this match.';
    body.appendChild(empty);
  } else {
    const rows = summaries.map((summary, idx) => {
      const phase = typeof summary.phase === 'string' ? summary.phase.toLowerCase() : summary.phase;
      let label = `R${summary.round}`;
      if (phase === 'retribution') label = 'Ret';
      if (phase === 'sudden_death') {
        const sd = summary.suddenDeathRound ?? idx + 1;
        label = `SD${sd}`;
      }
      return { summary, label };
    });

    const hasSuddenDeath = summaries.some((s) => s.phase === 'sudden_death');
    const table = document.createElement('table');
    table.className = 'boxscore-table';

    // Header (players as columns)
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const labelTh = document.createElement('th');
    labelTh.textContent = 'Round';
    headRow.appendChild(labelTh);

    const thP1 = document.createElement('th');
    thP1.textContent = p1Name;
    headRow.appendChild(thP1);

    const thP2 = document.createElement('th');
    thP2.textContent = p2Name;
    headRow.appendChild(thP2);

    thead.appendChild(headRow);
    table.appendChild(thead);

    const renderCell = (summary: RoundSummary, playerIndex: 1 | 2) => {
      const td = document.createElement('td');
      td.className = 'boxscore-cell';
      const bust = (summary.busts || []).find((b) => b.player === playerIndex);
      if (bust) td.classList.add('boxscore-cell-bust');

      const playedRolls = playerIndex === 1 ? summary.p1Rolls : summary.p2Rolls;
      if (summary.phase === 'retribution' && (!playedRolls || playedRolls.length === 0)) {
        td.textContent = '-';
        return td;
      }

      const points = summary.phase === 'retribution'
        ? (playerIndex === 1 ? summary.p1Points : summary.p2Points)
        : playerIndex === 1 ? summary.p1Points : summary.p2Points;
      const skeech = playerIndex === 1 ? summary.p1Skeech : summary.p2Skeech;
      const html = `${formatPoints(points, skeech, true)}${bust ? `<div class="boxscore-bust-note">${bust.from} -> ${bust.to}</div>` : ''}`;
      td.innerHTML = html;
      return td;
    };

    const tbody = document.createElement('tbody');
    rows.forEach(({ summary, label }) => {
      const tr = document.createElement('tr');
      const labelTd = document.createElement('td');
      labelTd.className = 'boxscore-name';
      labelTd.textContent = label;
      tr.appendChild(labelTd);
      tr.appendChild(renderCell(summary, 1));
      tr.appendChild(renderCell(summary, 2));
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);

    // Totals footer
    const tfoot = document.createElement('tfoot');
    const totalRow = document.createElement('tr');
    totalRow.className = 'boxscore-totals-row';
    const totalLabel = document.createElement('td');
    totalLabel.textContent = 'Total';
    totalRow.appendChild(totalLabel);

    const renderTotalCell = (playerIndex: 1 | 2) => {
      const td = document.createElement('td');
      td.className = 'boxscore-total';
      if (hasSuddenDeath) {
        const sdScore = finalTotals.phase === 'sudden_death' ? (playerIndex === 1 ? finalTotals.p1 : finalTotals.p2) : 0;
        const sdRoundText = finalTotals.suddenDeathRound ? ` (SD${finalTotals.suddenDeathRound})` : '';
        td.innerHTML = `<span class="crossed-out">21</span> ${sdScore}${sdRoundText}`;
      } else {
        const total = playerIndex === 1 ? finalTotals.p1 : finalTotals.p2;
        td.textContent = String(total);
      }
      if (showWinnerBadges && winner === playerIndex) {
        const badge = document.createElement('span');
        badge.className = 'boxscore-winner-badge';
        badge.textContent = 'Winner';
        td.appendChild(badge);
      }
      return td;
    };

    totalRow.appendChild(renderTotalCell(1));
    totalRow.appendChild(renderTotalCell(2));
    tfoot.appendChild(totalRow);
    table.appendChild(tfoot);

    body.appendChild(table);
  }

  modal.appendChild(body);

  const close = () => {
    try {
      overlay.remove();
    } catch {
      /* ignore */
    }
  };

  closeBtn.onclick = close;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener(
    'keydown',
    function onKeydown(ev) {
      if (ev.key === 'Escape') {
        close();
        document.removeEventListener('keydown', onKeydown);
      }
    },
    { once: true }
  );

  document.body.appendChild(overlay);
}
